import React, { useState } from 'react';
import { Modal, Form, Input, ColorPicker, Button, message } from 'antd';
import type { AggregationColor } from 'antd/es/color-picker/color';

export interface ApiFormsCreateTemplateRequestDto {
  name: string;
  slug: string;
  category: string;
  description: string;
  color: string;
  prompt: string;
}

const { TextArea } = Input;

interface CreateTemplateWithAIModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  loading: boolean;
  onSubmit: (values: ApiFormsCreateTemplateRequestDto) => Promise<void>;
}

const CreateTemplateWithAIModal: React.FC<CreateTemplateWithAIModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  loading,
  onSubmit,
}) => {
  const [form] = Form.useForm();

  const handleSubmit = async (values: any) => {
    try {
      const requestData: ApiFormsCreateTemplateRequestDto = {
        ...values,
        color: `#${values.color}`,
      };
      await onSubmit(requestData);
      form.resetFields();
      onSuccess();
    } catch (error) {
      console.error('Failed to create template:', error);
      message.error('Failed to create template');
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const generateSlugFromName = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>🤖</span>
          <span>Create Template with AI</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() => {
            form.submit();
          }}
        >
          Create with AI
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <p style={{ color: '#666', fontSize: '14px' }}>
          Our AI will generate a complete form template with multi-step flow,
          professional content, and visual design based on your requirements.
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={loading}
      >
        <Form.Item
          name="name"
          label="Template Name"
          rules={[
            { required: true, message: 'Please enter template name' },
            { max: 100, message: 'Name cannot exceed 100 characters' },
          ]}
        >
          <Input
            placeholder="e.g., Cleaning Services, Solar Installation, etc."
            onChange={(e) => {
              const slug = generateSlugFromName(e.target.value);
              form.setFieldsValue({ slug });
            }}
          />
        </Form.Item>

        <Form.Item
          name="slug"
          label="Template Slug"
          rules={[
            { required: true, message: 'Please enter template slug' },
            { max: 100, message: 'Slug cannot exceed 100 characters' },
            {
              pattern: /^[a-z0-9-]+$/,
              message:
                'Slug can only contain lowercase letters, numbers, and hyphens',
            },
          ]}
        >
          <Input placeholder="e.g., cleaning-services, solar-installation" />
        </Form.Item>

        <Form.Item
          name="category"
          label="Business Category"
          rules={[
            { required: true, message: 'Please enter a category' },
            { max: 50, message: 'Category cannot exceed 50 characters' },
          ]}
        >
          <Input placeholder="e.g., Home Service, Healthcare, Real Estate, etc." />
        </Form.Item>

        <Form.Item
          name="description"
          label="Template Description"
          rules={[
            { required: true, message: 'Please enter template description' },
            { max: 200, message: 'Description cannot exceed 200 characters' },
          ]}
        >
          <Input placeholder="Brief description of what this template is for" />
        </Form.Item>

        <Form.Item
          name="color"
          label="Brand Color"
          getValueFromEvent={(color) => {
            return color.toHex();
          }}
          initialValue={'016DF9'}
        >
          <ColorPicker showText />
        </Form.Item>

        <Form.Item
          name="prompt"
          label="AI Instructions"
          rules={[
            { required: false, message: 'Please provide AI instructions' },
            {
              max: 1000,
              message: 'Instructions cannot exceed 1000 characters',
            },
          ]}
        >
          <TextArea
            rows={4}
            placeholder="Describe what kind of form you want. E.g., 'Create a lead generation form for cleaning services with 5 questions max. Should capture service type, property size, frequency, special requirements, and contact info.'"
            showCount
            maxLength={1000}
          />
        </Form.Item>
      </Form>

      <div
        style={{
          background: '#f8f9fa',
          padding: '12px',
          borderRadius: '6px',
          marginTop: '16px',
        }}
      >
        <div style={{ fontSize: '12px', color: '#666' }}>
          <strong>What AI will create:</strong>
          <ul style={{ margin: '8px 0', paddingLeft: '16px' }}>
            <li>Multi-step form with professional question flow</li>
            <li>Visual first step with generated icons</li>
            <li>SEO-optimized landing page content</li>
            <li>Mobile-friendly responsive design</li>
            <li>Lead qualification and contact collection</li>
          </ul>
        </div>
      </div>
    </Modal>
  );
};

export default CreateTemplateWithAIModal;
